import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/layout/PublicLayout";
import CMSLayout from "@/components/layout/CMSLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import StartHere from "@/pages/StartHere";
import EternalLife from "@/pages/EternalLife";
import KnowJesus from "@/pages/KnowJesus";
import Gospel from "@/pages/Gospel";
import WhatWeBelieve from "@/pages/WhatWeBelieve";
import DoctrineDetail from "@/pages/DoctrineDetail";
import ArticlesIndex from "@/pages/ArticlesIndex";
import ArticleDetail from "@/pages/ArticleDetail";
import BibleStudiesIndex from "@/pages/BibleStudiesIndex";
import BibleStudyDetail from "@/pages/BibleStudyDetail";
import Devotionals from "@/pages/Devotionals";
import QAIndex from "@/pages/QAIndex";
import QADetail from "@/pages/QADetail";
import KidsIndex from "@/pages/KidsIndex";
import KidsStoryDetail from "@/pages/KidsStoryDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Search from "@/pages/Search";
import Languages from "@/pages/Languages";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import ElderLogin from "@/pages/ElderLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";

import CMSDashboard from "@/pages/cms/CMSDashboard";
import CMSArticlesList from "@/pages/cms/CMSArticlesList";
import CMSArticleEditor from "@/pages/cms/CMSArticleEditor";
import CMSPrayerRequests from "@/pages/cms/CMSPrayerRequests";
import CMSUsers from "@/pages/cms/CMSUsers";
import { CMSMessages, CMSAsk, CMSCorrections, CMSNewsletter } from "@/pages/cms/CMSSimpleLists";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function PublicRoute({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

function CMSRoute({ children, perm }) {
  return (
    <ProtectedRoute perm={perm}>
      <CMSLayout>{children}</CMSLayout>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();
  // Check URL fragment synchronously during render (prevents race conditions):
  // returning from Emergent Google Auth lands at {route}#session_id=...
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public site */}
        <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
            <Route path="/start-here" element={<PublicRoute><StartHere /></PublicRoute>} />
            <Route path="/eternal-life" element={<PublicRoute><EternalLife /></PublicRoute>} />
            <Route path="/know-jesus" element={<PublicRoute><KnowJesus /></PublicRoute>} />
            <Route path="/the-gospel" element={<PublicRoute><Gospel /></PublicRoute>} />
            <Route path="/what-we-believe" element={<PublicRoute><WhatWeBelieve /></PublicRoute>} />
            <Route path="/what-we-believe/:slug" element={<PublicRoute><DoctrineDetail /></PublicRoute>} />
            <Route path="/articles" element={<PublicRoute><ArticlesIndex /></PublicRoute>} />
            <Route path="/articles/:slug" element={<PublicRoute><ArticleDetail /></PublicRoute>} />
            <Route path="/bible-studies" element={<PublicRoute><BibleStudiesIndex /></PublicRoute>} />
            <Route path="/bible-studies/:slug" element={<PublicRoute><BibleStudyDetail /></PublicRoute>} />
            <Route path="/devotionals" element={<PublicRoute><Devotionals /></PublicRoute>} />
            <Route path="/christian-living" element={<PublicRoute><ArticlesIndex /></PublicRoute>} />
            <Route path="/questions" element={<PublicRoute><QAIndex /></PublicRoute>} />
            <Route path="/questions/:slug" element={<PublicRoute><QADetail /></PublicRoute>} />
            <Route path="/kids" element={<PublicRoute><KidsIndex /></PublicRoute>} />
            <Route path="/kids/:slug" element={<PublicRoute><KidsStoryDetail /></PublicRoute>} />
            <Route path="/about" element={<PublicRoute><About /></PublicRoute>} />
            <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
            <Route path="/search" element={<PublicRoute><Search /></PublicRoute>} />
            <Route path="/languages" element={<PublicRoute><Languages /></PublicRoute>} />
            <Route path="/legal/:slug" element={<PublicRoute><Legal /></PublicRoute>} />

            {/* Auth */}
            <Route path="/elder-login" element={<PublicLayout><ElderLogin /></PublicLayout>} />
            <Route path="/forgot-password" element={<PublicLayout><ForgotPassword /></PublicLayout>} />

            {/* CMS */}
            <Route path="/cms" element={<CMSRoute><CMSDashboard /></CMSRoute>} />
            <Route path="/cms/articles" element={<CMSRoute><CMSArticlesList /></CMSRoute>} />
            <Route path="/cms/articles/:id" element={<CMSRoute><CMSArticleEditor /></CMSRoute>} />
            <Route path="/cms/prayer-requests" element={<CMSRoute perm="moderate"><CMSPrayerRequests /></CMSRoute>} />
            <Route path="/cms/messages" element={<CMSRoute perm="moderate"><CMSMessages /></CMSRoute>} />
            <Route path="/cms/ask" element={<CMSRoute perm="moderate"><CMSAsk /></CMSRoute>} />
            <Route path="/cms/corrections" element={<CMSRoute perm="moderate"><CMSCorrections /></CMSRoute>} />
            <Route path="/cms/newsletter" element={<CMSRoute perm="moderate"><CMSNewsletter /></CMSRoute>} />
            <Route path="/cms/users" element={<CMSRoute perm="manage_users"><CMSUsers /></CMSRoute>} />

            {/* 404 */}
            <Route path="*" element={<PublicRoute><NotFound /></PublicRoute>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LangProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </LangProvider>
    </AuthProvider>
  );
}

export default App;
